/**
 * **The Talia's tabs** — every open task of one client as a tab with its kind and its one figure,
 * over the one card that is open (owner, 23–24 September 2026). Built on `shared/ui/TabStrip`, the
 * app's one tablist — one tab stop, arrows to move, Enter or Space to choose — in its `cards` skin;
 * never a second tablist.
 *
 * Drawn only from TWO tasks (`model/taskContent`'s `taliaLayout`): a strip with one tab is a control
 * with nothing to choose, so a single task is its card alone.
 */

import { useTranslation } from 'react-i18next';

import { TabStrip } from '~/shared/ui/TabStrip';

import { useQueueFigure } from '../lib/useTaskContent';
import type { AttentionItem } from '../model/attentionItems';
import { ReasonTile } from './ReasonTile';
import { HeroFigure } from './HeroFigure';
import styles from './taliaTabs.module.css';

interface TaskTaliaTabsProps {
  readonly items: readonly AttentionItem[];
  readonly selectedKey: string;
  readonly onSelect: (key: string) => void;
  readonly now: Date;
}

export function TaskTaliaTabs({ items, selectedKey, onSelect, now }: TaskTaliaTabsProps) {
  const { t } = useTranslation();
  const byKey = new Map(items.map((item) => [item.key, item]));

  return (
    <TabStrip
      className={styles.bar}
      variant="cards"
      label={t('web:tasks.talia.tabsLabel')}
      tabs={items.map((item) => item.key)}
      selected={selectedKey}
      onSelect={onSelect}
      renderLabel={(key) => {
        const item = byKey.get(key);
        return item === undefined ? null : <TabLabel item={item} now={now} />;
      }}
    />
  );
}

function TabLabel({ item, now }: { readonly item: AttentionItem; readonly now: Date }) {
  const { t } = useTranslation();
  const hero = useQueueFigure(item, now);
  return (
    <span className={styles.label}>
      <ReasonTile item={item} size="xs" decorative />
      <span className={styles.words}>
        <span className={styles.kind}>{t(`web:tasks.talia.label.${item.reason}`)}</span>
        {hero === null ? null : <HeroFigure hero={hero} skin={styles} />}
      </span>
    </span>
  );
}
